import { cva, type VariantProps } from 'class-variance-authority';
import { cls } from '@/lib/utils';
import type { WithMotionRefProps } from '@/lib/with-ref';
import type { ResponsiveProps } from '@/components/layout';

type ButtonCustomProps = {
  useDefaultAnimation?: boolean;
  asChild?: boolean;
};

export type ButtonElement = 'button';

export type ButtonProps = WithMotionRefProps<
  'button',
  ResponsiveProps & VariantProps<typeof buttonVariants> & ButtonCustomProps
>;

export const buttonVariants = cva(
  cls`
    inline-flex items-center justify-center
    rounded-full
    text-sm font-medium font-header
    ring-offset-background
    transition-colors
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
    disabled:pointer-events-none disabled:opacity-50
  `,
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-10 px-4 py-2',
        icon: 'h-10 w-10 rounded-full',
        lg: 'h-11 rounded-md px-8',
        sm: 'h-9 rounded-md px-3',
      },
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/80 shadow-lg',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/50 shadow-lg',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary-foreground underline-offset-4 hover:underline',
        outline: cls`
          border-2 border-input
          text-outline-foreground
          bg-transparent
          hover:bg-accent hover:text-accent-foreground
        `,
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-lg',
        tertiary:
          'bg-tertiary text-tertiary-foreground hover:bg-tertiary/80 shadow-lg',
      },
    },
  },
);
